import * as React from 'react';
import type { ReactNode } from 'react';

export type TreeNodeVariant = 'horizontal' | 'vertical' | 'assistant';
export const TreeNodeVariantStrings: {
  horizontal: TreeNodeVariant;
  vertical: TreeNodeVariant;
  assistant: TreeNodeVariant;
} = {
  horizontal: 'horizontal',
  vertical: 'vertical',
  assistant: 'assistant',
};

export interface TreeNodeProps {
  /** The node label */
  label: React.ReactNode;
  className?: string;
  variant: TreeNodeVariant;
  isLast: boolean;
  children?: ReactNode;
}

const NODE_CLASS_MAP: Record<TreeNodeVariant, string> = {
  horizontal: 'orgTreeNode__node--horizontal',
  vertical: 'orgTreeNode__node--vertical',
  assistant: 'orgTreeNode__node--assistant',
};

const LINE_CLASS_MAP: Record<TreeNodeVariant, string> = {
  horizontal: 'orgTreeNode__lines--horizontal',
  vertical: 'orgTreeNode__lines--vertical',
  assistant: 'orgTreeNode__lines--assistant',
};

const getChildrenByVariant = (target: TreeNodeVariant, children: ReactNode) =>
  React.Children.toArray(children).filter(
    (child): child is React.ReactElement<typeof TreeNode> =>
      React.isValidElement(child) && child.props.variant === target
  );

function TreeNode({
  children,
  label,
  variant,
  isLast,
  className,
}: TreeNodeProps) {
  const assistantChildren = getChildrenByVariant(
    TreeNodeVariantStrings.assistant,
    children
  );

  const horizontalChildren = getChildrenByVariant(
    TreeNodeVariantStrings.horizontal,
    children
  );

  const verticalChildren = getChildrenByVariant(
    TreeNodeVariantStrings.vertical,
    children
  );

  const nodeClass = NODE_CLASS_MAP[variant];
  const lineClass = LINE_CLASS_MAP[variant];

  const isVertical = variant === TreeNodeVariantStrings.vertical;

  let specialTopLines = '';

  if (verticalChildren.length > 0) {
    specialTopLines = 'orgTreeNode__lines--horizontalWithChildren';
  }

  return (
    <li
      className={`orgTreeNode ${[
        nodeClass,
        lineClass,
        className,
        specialTopLines,
      ]
        .filter(Boolean)
        .join(' ')}`}
      style={{
        ...(verticalChildren.length ? { textAlign: 'left' } : {}),
        ...(variant === TreeNodeVariantStrings.assistant
          ? { textAlign: 'center' }
          : {}),
      }}
    >
      {isVertical && (
        <>
          <div
            style={{
              position: 'absolute',
              width: 'var(--tree-line-width)',
              backgroundColor: 'white',
              top: '0px',
              left: '-25px',
              zIndex: -1,
              height: isLast ? '15px' : '100%',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '15px',
              left: '-25px',
              right: '50%',
              height: 'var(--tree-line-width)',
              backgroundColor: 'white',
              zIndex: -1,
            }}
          />
        </>
      )}
      <div
        style={{
          paddingLeft: isVertical ? 'var(--tree-node-padding)' : '25px',
        }}
      >
        {label}
      </div>
      {assistantChildren.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: 'var(--tree-line-width)',
              backgroundColor: 'white',
            }}
          />
          {assistantChildren.map((child, index) => (
            <div
              key={index}
              style={{
                paddingTop: '10px',
                display: 'flex',
                justifyContent:
                  assistantChildren.length === 1 || index % 2 === 0
                    ? 'flex-end'
                    : 'flex-start',
                alignItems: 'center',
                position: 'relative',
                paddingInline: 'var(--tree-node-padding)',
                textAlign: index % 2 === 0 ? 'right' : 'left',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '15px',
                  width: 'calc(var(--tree-node-padding) + 25px)',
                  height: 'var(--tree-line-width)',
                  backgroundColor: 'white',
                  left: index % 2 === 0 ? 'auto' : 0,
                  right: index % 2 === 0 ? 0 : 'auto',
                }}
              />
              {child}
            </div>
          ))}
        </div>
      )}
      {verticalChildren.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            position: 'relative',
            zIndex: 1,
            paddingLeft: 'var(--tree-node-padding)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              position: 'relative',
              textAlign: 'center',
              left: '55px',
            }}
          >
            <div>{verticalChildren}</div>
          </div>
        </div>
      )}
      {horizontalChildren.length > 0 && verticalChildren.length > 0 ? (
        <>
          {/* Case where both horizontal and vertical children exist */}
          <div
            style={{
              height: '5px',
              marginLeft: '35px',
              width: 'var(--tree-line-width)',
              backgroundColor: 'white',
              position: 'relative',
            }}
          />
          <div
            style={{
              width: 'calc(50% - 35px)',
              marginLeft: '35px',
              height: 'var(--tree-line-width)',
              backgroundColor: 'white',
              position: 'relative',
            }}
          />
          <ul className={'orgTreeNode__children'}>{horizontalChildren}</ul>
        </>
      ) : horizontalChildren.length > 0 ? (
        <ul className={'orgTreeNode__children'}>{horizontalChildren}</ul>
      ) : null}
    </li>
  );
}

export default TreeNode;
